import { queryOptions, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { VideoRatio, VideoResolution } from '@/lib/constants'

/** 템플릿 프레임 이미지 */
export interface TemplateFrameImage {
  role: 'first_frame' | 'last_frame'
  s3Key: string
  thumbnailS3Key: string
  thumbnailUrl: string
}

/** 템플릿 응답 */
export interface Template {
  id: string
  name: string
  description: string | null
  /** 언어 코드 → 번역된 이름 (ko 원문은 name) */
  nameI18n?: Record<string, string> | null
  /** 언어 코드 → 번역된 설명 (ko 원문은 description) */
  descriptionI18n?: Record<string, string> | null
  textPrompt: string
  /** 언어 코드 → 번역된 프롬프트 (ko 원문은 textPrompt) */
  textPromptI18n?: Record<string, string> | null
  promptSuffix: string | null
  ratio: VideoRatio
  resolution: VideoResolution
  duration: number
  thumbnailUrl: string | null
  videoUrl: string | null
  updatedAt: string
  frameImages: TemplateFrameImage[]
}

/** 카테고리별 템플릿 그룹 */
export interface TemplateCategoryGroup {
  id: string
  name: string
  description: string | null
  /** 언어 코드 → 번역된 이름 (ko 원문은 name) */
  nameI18n?: Record<string, string> | null
  /** 언어 코드 → 번역된 설명 (ko 원문은 description) */
  descriptionI18n?: Record<string, string> | null
  templates: Template[]
}

export function useTemplates() {
  return useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<TemplateCategoryGroup[]>('/templates'),
    staleTime: 1000 * 60 * 5,
  })
}

export interface PopularTemplate extends Template {
  categoryId: string
  categoryName: string
  /** 언어 코드 → 번역된 카테고리 이름 (ko 원문은 categoryName) */
  categoryNameI18n?: Record<string, string> | null
}

export function usePopularTemplates(enabled: boolean = true) {
  return useQuery({
    queryKey: ['templates', 'popular'],
    queryFn: () => api.get<PopularTemplate[]>('/templates/popular'),
    staleTime: 1000 * 60 * 5,
    enabled,
  })
}

/** 특정 템플릿 상세 쿼리 (useTemplate 과 queryClient.fetchQuery 가 같은 캐시를 쓴다) */
export function templateQueryOptions(id: string | null) {
  return queryOptions({
    queryKey: ['template', id],
    queryFn: () => api.get<Template>(`/templates/${id}`),
    staleTime: 1000 * 60 * 5,
  })
}

export function useTemplate(id: string | null) {
  return useQuery({ ...templateQueryOptions(id), enabled: !!id })
}
